import { Radio, RadioGroup } from '@/components/ui/radio';
import { toaster } from '@/components/ui/toaster';
import { useSaveMetaAdsPreferences } from '@/hooks/actions/use-meta-ads-actions';
import { bareAdAccountId, metaAdsBillingUrl } from '@/libs/meta-ads-links';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { Badge, Box, Flex, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { PaymentNeededBadge } from './PaymentNeededBadge';
import type { MetaAdsAdAccountDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface MetaAdsAdAccountListProps {
  accounts: MetaAdsAdAccountDto[];
  defaultAdAccountId: string | null;
}

/**
 * One row of the picker: the account and why it may not be spendable.
 *
 * The two unspendable cases are deliberately told apart. A missing payment
 * method is the brand's to fix, so the account stays selectable and the row
 * carries the link that fixes it. An account Meta disabled cannot be fixed from
 * here at all, so the row is inert and reads as such.
 */
function AdAccountRow({ account, isFirst }: { account: MetaAdsAdAccountDto; isFirst: boolean }) {
  const { t } = useTranslation();
  const billingUrl = metaAdsBillingUrl(account.id);

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      px={4}
      py={3}
      borderTopWidth={isFirst ? 0 : '1px'}
      borderColor="border"
      bg={account.disabled ? 'bg.subtle' : undefined}
    >
      <Radio value={account.id} disabled={account.disabled}>
        <VStack align="start" gap={0} minW={0}>
          <Text fontSize="sm" color={account.disabled ? 'fg.muted' : 'fg'} lineClamp={1}>
            {account.name}
          </Text>
          {/* A brand can own several accounts under one name; the id is what
              makes the row identifiable and quotable to support. */}
          <Text fontSize="xs" color="fg.muted">
            {bareAdAccountId(account.id)}
          </Text>
        </VStack>
      </Radio>

      <HStack gap={2} flexShrink={0}>
        {account.currencyCode && (
          <Text fontSize="xs" color="fg.muted">
            {account.currencyCode}
          </Text>
        )}
        {account.disabled ? (
          <Badge size="xs" colorPalette="gray" variant="subtle">
            {t('integrations.metaAds.defaultAccount.disabled')}
          </Badge>
        ) : !account.paymentReady ? (
          <HStack gap={2}>
            <PaymentNeededBadge />
            {billingUrl && (
              <Link
                href={billingUrl}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="xs"
                colorPalette="primary"
              >
                {t('integrations.metaAds.defaultAccount.addPayment')}
              </Link>
            )}
          </HStack>
        ) : null}
      </HStack>
    </Flex>
  );
}

/**
 * Ad-account picker on the Meta Ads detail page: a radio list where the brand
 * sets the account billed by default when a boost launches. Selecting commits
 * immediately (no save step).
 *
 * Picking an account is a configuration act, not a spending one, so an account
 * without a payment method stays selectable and says what it needs. Only an
 * account Meta disabled is out of reach.
 */
export function MetaAdsAdAccountList({ accounts, defaultAdAccountId }: MetaAdsAdAccountListProps) {
  const { t } = useTranslation();
  const savePreferences = useSaveMetaAdsPreferences();

  if (accounts.length === 0) {
    return null;
  }

  const handleSelect = (adAccountId: string) => {
    if (adAccountId === defaultAdAccountId) {
      return;
    }
    savePreferences.mutate(
      { adAccountId },
      {
        onError: (error) =>
          toaster.create({
            description: getApiErrorMessage(error, t, 'integrations.metaAds.defaultAccount.error'),
            type: 'error',
          }),
      }
    );
  };

  return (
    <Box>
      <Text fontWeight="medium" color="fg">
        {t('integrations.metaAds.defaultAccount.title')}
      </Text>
      <Text fontSize="sm" color="fg.muted" mb={3}>
        {t('integrations.metaAds.defaultAccount.description')}
      </Text>

      <RadioGroup
        value={defaultAdAccountId ?? undefined}
        onValueChange={(event) => event.value && handleSelect(event.value)}
        disabled={savePreferences.isPending}
      >
        <Flex direction="column" borderWidth="1px" borderColor="border" rounded="lg">
          {accounts.map((account, index) => (
            <AdAccountRow key={account.id} account={account} isFirst={index === 0} />
          ))}
        </Flex>
      </RadioGroup>
    </Box>
  );
}
